/**
 * Year options for filtering overtime data
 * This utility provides consistent year filtering across the application
 */

export interface YearOption {
  value: string;
  label: string;
}

/**
 * Default year filter options
 */
export const YEAR_FILTER_OPTIONS: YearOption[] = [
  { value: 'all', label: 'All Years' },
  { value: 'current', label: 'Current Year' },
];

/**
 * Generate year options from overtime data dates
 * @param dates - Array of date strings to extract years from
 * @returns Array of year options sorted by year (newest first)
 */
export const generateYearOptions = (dates: string[]): YearOption[] => {
  const years = new Set<number>();
  
  dates.forEach(date => {
    try {
      const year = new Date(date).getFullYear();
      // Only include reasonable years (2020-2100)
      if (!isNaN(year) && year >= 2020 && year <= 2100) {
        years.add(year);
      }
    } catch (e) {
      // Skip invalid dates
    }
  });
  
  // Convert to options and sort by year (newest first)
  const yearOptions = Array.from(years)
    .sort((a, b) => b - a)
    .map(year => ({ 
      value: String(year), 
      label: String(year) 
    }));
  
  return [...YEAR_FILTER_OPTIONS, ...yearOptions];
};

/**
 * Check if a date matches the selected year filter
 * @param date - Date string to check
 * @param selectedYear - Selected year filter value
 * @returns true if date matches the year filter
 */
export const matchesYearFilter = (date: string, selectedYear: string): boolean => {
  if (selectedYear === 'all') return true;
  
  try {
    const recDate = new Date(date);
    const recYear = recDate.getFullYear();
    const currentYear = new Date().getFullYear();
    
    if (selectedYear === 'current') {
      return recYear === currentYear;
    }
    
    return recYear === parseInt(selectedYear);
  } catch (e) {
    return false;
  }
};

